import {
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  onIdTokenChanged,
  reauthenticateWithCredential,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  type Unsubscribe
} from 'firebase/auth'
import { httpsCallable } from 'firebase/functions'
import { getPmmFirebaseServices } from '../lib/firebase/client'
import type { PmmSession } from '../domain/auth'

export type PrototypeRole = 'owner' | 'client' | 'partner'

export interface AuthGateway {
  readonly mode: 'prototype' | 'firebase'
  subscribe(listener: (session: PmmSession | null) => void): () => void
  signIn(email: string, password: string, prototypeRole?: PrototypeRole): Promise<void>
  registerPartner(email: string, password: string): Promise<void>
  claimPartnerInvite(token: string): Promise<void>
  reauthenticate(password: string): Promise<void>
  claimOwner(): Promise<void>
  signOut(): Promise<void>
}

export class PrototypeAuthGateway implements AuthGateway {
  readonly mode = 'prototype' as const
  private session: PmmSession | null = null
  private listeners = new Set<(session: PmmSession | null) => void>()

  subscribe(listener: (session: PmmSession | null) => void): () => void {
    this.listeners.add(listener)
    listener(this.session)
    return () => this.listeners.delete(listener)
  }

  async signIn(email: string, _password: string, prototypeRole: PrototypeRole = 'owner'): Promise<void> {
    const now = Math.floor(Date.now() / 1000)
    this.session = {
      uid: prototypeRole === 'owner' ? 'prototype-owner' : prototypeRole === 'partner' ? 'prototype-partner' : 'prototype-client',
      email: email || `${prototypeRole}@pmm.local`,
      displayName: prototypeRole === 'owner' ? 'Koen' : prototypeRole === 'partner' ? 'Partner preview' : 'Client preview',
      authTime: now,
      tokenIssuedAt: now,
      claims: {
        pmm_user: prototypeRole !== 'partner',
        pmm_owner_coach: prototypeRole === 'owner',
        pmm_role_version: prototypeRole === 'partner' ? 0 : 1,
        pmm_partner: prototypeRole === 'partner',
        pmm_partner_role_version: prototypeRole === 'partner' ? 1 : 0
      },
      accountState: 'active',
      partnerAccountState: prototypeRole === 'partner' ? 'active' : 'none'
    }
    this.emit()
  }

  async registerPartner(email: string, password: string): Promise<void> { await this.signIn(email, password, 'partner') }
  async claimPartnerInvite(_token: string): Promise<void> { if (!this.session?.claims.pmm_partner) throw new Error('prototype_partner_required') }

  async reauthenticate(_password: string): Promise<void> {
    if (!this.session) throw new Error('not_authenticated')
    const now = Math.floor(Date.now() / 1000)
    this.session = { ...this.session, authTime: now, tokenIssuedAt: now }
    this.emit()
  }

  async claimOwner(): Promise<void> {
    throw new Error('prototype_owner_is_selected_at_sign_in')
  }

  async signOut(): Promise<void> {
    this.session = null
    this.emit()
  }

  private emit() {
    for (const listener of this.listeners) listener(this.session ? structuredClone(this.session) : null)
  }
}

export class FirebaseAuthGateway implements AuthGateway {
  readonly mode = 'firebase' as const
  private unsubscribe: Unsubscribe | null = null

  subscribe(listener: (session: PmmSession | null) => void): () => void {
    let active = true
    void getPmmFirebaseServices().then(({ auth }) => {
      if (!active) return
      this.unsubscribe = onIdTokenChanged(auth, async (user) => {
        if (!user) {
          listener(null)
          return
        }
        const token = await user.getIdTokenResult()
        const authTime = Math.floor(new Date(token.authTime).getTime() / 1000)
        const tokenIssuedAt = Math.floor(new Date(token.issuedAtTime).getTime() / 1000)
        listener({
          uid: user.uid,
          email: user.email ?? '',
          displayName: user.displayName ?? user.email?.split('@')[0] ?? 'PMM member',
          authTime,
          tokenIssuedAt,
          claims: {
            pmm_user: token.claims.pmm_user === true,
            pmm_owner_coach: token.claims.pmm_owner_coach === true,
            pmm_role_version: Number(token.claims.pmm_role_version ?? 0),
            pmm_partner: token.claims.pmm_partner === true,
            pmm_partner_role_version: Number(token.claims.pmm_partner_role_version ?? 0)
          },
          accountState: token.claims.pmm_account_state === 'suspended'
            ? 'suspended'
            : token.claims.pmm_account_state === 'deletion_pending' ? 'deletion_pending' : 'active',
          partnerAccountState: token.claims.pmm_partner_account_state === 'active' ? 'active'
            : token.claims.pmm_partner_account_state === 'suspended' ? 'suspended' : 'none'
        })
      })
    }).catch(() => listener(null))
    return () => {
      active = false
      this.unsubscribe?.()
    }
  }

  async signIn(email: string, password: string): Promise<void> {
    const { auth } = await getPmmFirebaseServices()
    await signInWithEmailAndPassword(auth, email, password)
  }

  async registerPartner(email: string, password: string): Promise<void> {
    const { auth } = await getPmmFirebaseServices()
    const credential = await createUserWithEmailAndPassword(auth, email, password)
    await sendEmailVerification(credential.user)
  }

  async claimPartnerInvite(token: string): Promise<void> {
    const { auth, functions } = await getPmmFirebaseServices()
    if (!auth.currentUser?.emailVerified) throw new Error('verified_email_required')
    await auth.currentUser.getIdToken(true)
    const claim = httpsCallable<{ token: string }, { refreshRequired: boolean }>(functions, 'claimPmmPartnerInvite')
    await claim({ token })
    await auth.currentUser.getIdToken(true)
  }

  async reauthenticate(password: string): Promise<void> {
    const { auth } = await getPmmFirebaseServices()
    const user = auth.currentUser
    if (!user?.email) throw new Error('not_authenticated')
    await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, password))
    await user.getIdToken(true)
  }

  async claimOwner(): Promise<void> {
    const { auth, functions } = await getPmmFirebaseServices()
    // Password setup and email verification can happen in a different browser
    // session. Refresh before invoking the callable so the server receives the
    // current email_verified and email claims, rather than a cached ID token.
    await auth.currentUser?.getIdToken(true)
    const claim = httpsCallable<Record<string, never>, { refreshRequired: boolean }>(functions, 'claimPersonalAlphaOwner')
    await claim({})
    await auth.currentUser?.getIdToken(true)
  }

  async signOut(): Promise<void> {
    const { auth } = await getPmmFirebaseServices()
    await firebaseSignOut(auth)
  }
}

export function createAuthGateway(): AuthGateway {
  return import.meta.env.VITE_PMM_DATA_MODE === 'emulator' || import.meta.env.VITE_PMM_DATA_MODE === 'firebase'
    ? new FirebaseAuthGateway()
    : new PrototypeAuthGateway()
}
